import React, { useState, useEffect } from 'react';

import { 
  ProfileContainer, 
  Header,
  AvatarWrapper,
  Avatar, 
  MainContent, 
  ButtonGroup, 
  ProfileButton, 
  ProfileForm, 
  InputGroup, 
  ProfileLabel, 
  ProfileInput, 
  CouponsSection, 
  CouponList, 
  CouponCard
} from '../styles';

import { getUser, getUserCoupons, updateProfile } from '../api';
import { Spinner } from '../components';

const Profile = () => {
  const [userData, setUserData] = useState({});
  const [originalData, setOriginalData] = useState({});
  const [validCoupons, setValidCoupons] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [profileImage, setProfileImage] = useState('https://img.freepik.com/vetores-premium/imagem-de-perfil-placeholder-avatar-silhueta-em-tons-de-cinza-icone-formas-coloridas-gradiente_1076610-40164.jpg');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false); // Estado para controlar a visibilidade da senha

  useEffect(() => {
    const fetchData = async () => {
      try {

        const response = await getUser();

        if (!response.ok) {
          throw new Error('Erro ao buscar dados do usuário');
        }

        const data = await response.json();

        // Atualizar os dados do usuário
        setUserData({
          ...data,
          profileImage: 'https://via.placeholder.com/200', // Mock da imagem
        });
        setOriginalData({
          ...data,
          profileImage: 'https://via.placeholder.com/200',
        });

        // Requisição para pegar os cupons
        const couponResponse = await getUserCoupons();

        if (!couponResponse.ok) {
          throw new Error('Erro ao buscar cupons');
        }

        const coupons = await couponResponse.json();

        // Atualizar os cupons válidos
        if (coupons.userCoupons && Array.isArray(coupons.userCoupons.validCoupons)) {
          setValidCoupons(coupons.userCoupons.validCoupons);
        } else {
          setValidCoupons([]); // Em caso de erro ou formato inválido
        }

      } catch (error) {
        alert(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setProfileImage(reader.result); 
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    try {
      
      // Criar o objeto profileData com os dados do usuário
      const profileData = { ...userData };

      // Se a senha atual e a nova senha estiverem preenchidas, adicioná-las ao profileData
      if (currentPassword && newPassword) {
        profileData.password = currentPassword; // Usando "password" como chave para a senha atual
        profileData.newPassword = newPassword; // Usando "newPassword" para a nova senha
      }

      const response = await updateProfile(profileData);

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Erro ao salvar os dados');
      }

      alert('Dados salvos com sucesso!');
      setIsEditing(false);
      setOriginalData(userData); // Atualiza os dados originais com os novos dados
      setCurrentPassword('');
      setNewPassword('');

    } catch (error) {
      alert(error.message); // Exibe erro em caso de falha
    }
  };

  const handleCancel = () => {
    setUserData(originalData); // Volta aos dados originais
    setIsEditing(false); // Sai do modo de edição
    setIsChangingPassword(false); // Sai do modo de troca de senha
    setCurrentPassword(''); // Limpa o campo de senha atual
    setNewPassword(''); // Limpa o campo de nova senha
  };

  const formatCouponValue = (value, type) => {
    return type === 'percentage' ? parseInt(value, 10) : value.toFixed(2);
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <ProfileContainer>
      <Header>
        <h2 style={{ fontSize: '26px', fontWeight: 'bold', color: '#1B76BB', fontFamily: "Bellota" }}>Perfil do Usuário</h2>
        <AvatarWrapper style={{ textAlign: 'center' }}>
          <Avatar src={profileImage} alt="User Avatar" />
          <input
            type="file"
            accept="image/*"
            style={{ marginTop: '10px' }}
            onChange={handleImageUpload}
          />
        </AvatarWrapper>
      </Header>

      <MainContent>
        <ProfileForm>
          {['fullName', 'cep', 'address', 'neighborhood', 'city', 'complement', 'phone'].map((field) => {
            const fieldProfileLabels = {
              fullName: 'Nome Completo',
              cep: 'CEP',
              address: 'Endereço',
              neighborhood: 'Bairro',
              city: 'Cidade',
              complement: 'Complemento',
              phone: 'Telefone',
            };

            return (
              <InputGroup key={field}>
                <ProfileLabel htmlFor={field}>{fieldProfileLabels[field]}</ProfileLabel>
                <ProfileInput
                  id={field}
                  type={field === 'email' ? 'email' : 'text'}
                  value={userData[field] || ''}
                  onChange={(e) => setUserData({ ...userData, [field]: e.target.value })}
                  disabled={!isEditing}
                />
              </InputGroup>
            );
          })}
          <InputGroup>
            <ProfileLabel htmlFor="password">Gerenciar Senha</ProfileLabel>
            {!isChangingPassword ? (
              <ProfileButton
                type="button"
                onClick={() => setIsChangingPassword(true)}
                className="toggle-password"
                disabled={!isEditing} // Desabilita o botão se não estiver em edição
                style={{
                  cursor: !isEditing ? 'not-allowed' : 'pointer', // Define o cursor como not-allowed quando o botão estiver desabilitado
                  opacity: !isEditing ? 0.5 : 1, // Altera a opacidade para indicar que o botão está desabilitado
                }}
              >
                Alterar Senha
              </ProfileButton>
            ) : (
              <>
                <ProfileInput
                  id="currentPassword"
                  type={showPassword ? "text" : "password"} // Alterna entre "text" e "password"
                  placeholder="Senha Atual"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  style={{marginBottom: "10px"}}
                />
                <ProfileInput
                  id="newPassword"
                  type={showPassword ? "text" : "password"} // Alterna entre "text" e "password"
                  placeholder="Nova Senha"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  style={{marginBottom: "10px"}}
                />
                <ProfileButton type="button" onClick={() => setShowPassword(!showPassword)} style={{ marginTop: "10px" }}>
                  {showPassword ? "Ocultar Senha" : "Mostrar Senha"}
                </ProfileButton>
              </>
            )}
          </InputGroup>
        </ProfileForm>

        <ButtonGroup>
          {!isEditing ? (
            <ProfileButton onClick={() => setIsEditing(true)}>Editar</ProfileButton>
          ) : (
            <>
              <ProfileButton onClick={handleSave}>Salvar</ProfileButton>
              <ProfileButton
                onClick={handleCancel}
                className="cancel"
              >
                Cancelar
              </ProfileButton>
            </>
          )}
        </ButtonGroup>

        <CouponsSection>
          <h3 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1B76BB', fontFamily: "Bellota" }}>Cupons Disponíveis</h3>
          {validCoupons.length === 0 ? (
            <p>Não há nenhum cupom disponível no momento.</p>
          ) : (
            <CouponList>
              {validCoupons.map((coupon, index) => (
                <CouponCard key={index}>
                  <p style={{fontSize: "14px"}}><strong>Código do cupom:</strong> {coupon.coupon_code}</p>
                  <p style={{fontSize: "14px"}}><strong>Valor do desconto:</strong> {coupon.type === 'percentage' ? `${formatCouponValue(coupon.value, coupon.type)}%` : `R$ ${coupon.value}`}</p>
                  <p style={{fontSize: "14px"}}><strong>Data e horário de expiração:</strong> {new Date(coupon.expiration).toLocaleString()}</p>
                  <p style={{fontSize: "14px"}}><strong>Data de criação:</strong> {new Date(coupon.created_at).toLocaleString()}</p>
                </CouponCard>
              ))}
            </CouponList>
          )}
        </CouponsSection>
      </MainContent>
    </ProfileContainer>
  );
};

export default Profile;
